// CP-4 — CooldownCard.
//
// Renders the 24h post-bust lockout (PRD §16). Receives a cooldown
// end time (ISO8601 UTC) — from either useSinglesChain.cooldown
// (Slice ζ HTTP 423) or sc_chains.cooldown_ends_at on a busted chain
// — and displays a live countdown plus the honest "capital position"
// summary. The card is non-blocking — start-chain buttons are disabled
// while it's mounted, the user can still browse the rest of the page.

import { useEffect, useState } from "react";

export interface CooldownCardProps {
  cooldownEndsAt: string;
  capitalAfter?: number | null;
  stakeSource?: "capital" | "profit" | null;
  message?: string | null;
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return "00:00:00";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600).toString().padStart(2, "0");
  const m = Math.floor((totalSec % 3600) / 60).toString().padStart(2, "0");
  const s = (totalSec % 60).toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
}

const MONO_LABEL: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
};

export function CooldownCard({
  cooldownEndsAt,
  capitalAfter,
  stakeSource,
  message,
}: CooldownCardProps): JSX.Element {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const ends = new Date(cooldownEndsAt).getTime();
  const remainingMs = ends - now;
  const expired = remainingMs <= 0;

  return (
    <div
      className="sc-cooldown-card"
      role="status"
      aria-live="polite"
      data-state={expired ? "expired" : "active"}
      style={{
        background: expired ? "var(--bg-elev)" : "var(--fp-lost-soft)",
        border: `1px solid ${expired ? "var(--border)" : "var(--fp-lost)"}`,
        borderRadius: 10,
        padding: 14,
      }}
    >
      <div
        className="sc-cooldown-head"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <span
          className="sc-cooldown-eyebrow"
          style={{
            ...MONO_LABEL,
            color: expired ? "var(--text-tertiary)" : "var(--fp-lost)",
          }}
        >
          {expired ? "Cooldown complete" : "Cooldown · 24h rest after bust"}
        </span>
        <span
          className="sc-cooldown-clock"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: "0.04em",
            color: expired ? "var(--text-secondary)" : "var(--fp-lost)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {formatRemaining(remainingMs)}
        </span>
      </div>

      {message ? (
        <p
          className="sc-cooldown-msg"
          style={{
            margin: "10px 0 0",
            fontSize: 13,
            lineHeight: 1.45,
            color: "var(--text-secondary)",
          }}
        >
          {message}
        </p>
      ) : null}

      {(capitalAfter != null || stakeSource) && (
        <div
          className="sc-cooldown-meta"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
            marginTop: 12,
          }}
        >
          {stakeSource && (
            <span
              className={`sc-cooldown-source sc-cooldown-source--${stakeSource}`}
              style={{
                ...MONO_LABEL,
                padding: "4px 8px",
                borderRadius: 4,
                border: "1px solid var(--border)",
                background: "var(--bg-inset)",
                color: "var(--text-tertiary)",
              }}
            >
              Bust drew from {stakeSource}
            </span>
          )}
          {capitalAfter != null && (
            <span
              className="sc-cooldown-capital"
              style={{
                fontSize: 12.5,
                color: "var(--text-secondary)",
                fontFamily: "var(--font-mono)",
              }}
            >
              Capital position:{" "}
              <b style={{ color: "var(--text)" }}>
                GH₵{capitalAfter.toFixed(2)}
              </b>
            </span>
          )}
        </div>
      )}

      <p
        className="sc-cooldown-foot"
        style={{
          margin: "12px 0 0",
          fontSize: 12,
          lineHeight: 1.4,
          color: "var(--text-tertiary)",
        }}
      >
        {expired
          ? "You can start a new chain when the next session opens."
          : "New chains unlock automatically when the timer ends."}
      </p>
    </div>
  );
}
